import {FC} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Error from "@mui/icons-material/ErrorOutlineOutlined";
import {isApiError, isNativeError, isSerializedError} from "utils/error";
import {screamingSnakeToSentence} from "utils/values";

type ErrorStateProps = {
  error?: unknown;
  fallbackMessage?: string;
  onRetry?: () => void;
};

const extractErrorDetails = (
  error: unknown,
  fallback = "An application error occurred. Our team has been notified.",
) => {
  if (isApiError(error)) {
    return {
      code: error.status?.toString(),
      message: error.userMessage,
    };
  } else if (isSerializedError(error)) {
    return {
      code: error.code,
      message: error.message || fallback,
    };
  } else if (isNativeError(error)) {
    return {
      code: undefined,
      message: error.message || fallback,
    };
  }

  return {code: undefined, message: fallback};
};

const ErrorState: FC<ErrorStateProps> = props => {
  const {error, fallbackMessage, onRetry} = props;
  const {code, message} = extractErrorDetails(error, fallbackMessage);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      my={4}>
      <Error sx={{mb: 2}} fontSize="large" color="error" />
      <Typography variant="body1" gutterBottom fontWeight="medium">
        {code ? screamingSnakeToSentence(code) : "Something went wrong"}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {message}
      </Typography>

      {onRetry && (
        <Button variant="text" onClick={onRetry}>
          Retry
        </Button>
      )}
    </Box>
  );
};

export default ErrorState;
